import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Users, UserCheck, UserX, Plane, Clock, AlertTriangle,
  Upload, Download, FileText, ChevronLeft, ChevronRight, Search, X, Save,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tok = () => localStorage.getItem('crm_token');
const authH = () => ({ Authorization: `Bearer ${tok()}` });

const STATUS_META = {
  P:  { label: 'Present',     cls: 'bg-green-500 text-white' },
  A:  { label: 'Absent',      cls: 'bg-red-500 text-white' },
  L:  { label: 'Leave',       cls: 'bg-yellow-400 text-black' },
  H:  { label: 'Holiday',     cls: 'bg-blue-500 text-white' },
  WO: { label: 'Weekly Off',  cls: 'bg-gray-400 text-white' },
};
const STATUS_KEYS = ['P', 'A', 'L', 'H', 'WO'];
const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const now = new Date();

const KpiCard = ({ icon: Icon, label, value, color }) => (
  <div className={`p-4 rounded-xl border shadow-sm bg-white flex items-center gap-3`} data-testid={`kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <div className={`p-2 rounded-lg ${color}`}><Icon className="w-5 h-5 text-white" /></div>
    <div>
      <div className="text-[10px] uppercase text-slate-400 font-semibold">{label}</div>
      <div className="text-lg font-bold text-slate-800">{value}</div>
    </div>
  </div>
);

const AttendanceModule = () => {
  const [user] = useState(() => { try { return JSON.parse(localStorage.getItem('crm_user') || '{}'); } catch { return {}; } });
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [grid, setGrid] = useState(null);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dept, setDept] = useState('');
  const [dayFrom, setDayFrom] = useState('');
  const [dayTo, setDayTo] = useState('');
  const [importing, setImporting] = useState(false);
  const [sheetName, setSheetName] = useState('');
  const [editing, setEditing] = useState(null);
  const [autoFillDate, setAutoFillDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [autoFilling, setAutoFilling] = useState(false);
  const [autoFillResult, setAutoFillResult] = useState(null);
  const fileRef = useRef(null);

  const loadGrid = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ month, year, page, limit: 25, search, status: statusFilter, department: dept });
      const [g, d] = await Promise.all([
        fetch(`${API}/attendance/grid?${params}`, { headers: authH() }).then(r => r.json()),
        fetch(`${API}/attendance/dashboard?month=${month}&year=${year}`, { headers: authH() }).then(r => r.json()),
      ]);
      setGrid(g); setDash(d);
    } catch { toast.error('Failed to load attendance'); }
    finally { setLoading(false); }
  }, [month, year, page, search, statusFilter, dept]);

  useEffect(() => { loadGrid(); }, [loadGrid]);
  useEffect(() => { setPage(1); }, [month, year, search, statusFilter, dept]);

  const canEdit = grid?.canEdit;

  const handleAutoFill = async () => {
    if (!window.confirm(`Auto-fill attendance for ${autoFillDate} from the Login Report?\n\nON TIME logins → P (Present)\nLATE logins (after 10:10 AM) → LC (Late Coming)\nAbsent employees → skipped`)) return;
    setAutoFilling(true);
    setAutoFillResult(null);
    try {
      const r = await fetch(`${API}/hr/monthly-attendance/auto-fill-login`, {
        method: 'POST',
        headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ month, year, date: autoFillDate }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Auto-fill failed');
      setAutoFillResult(j);
      toast.success(`Auto-filled: ${j.filled} employees (${j.on_time} on-time, ${j.late} late)`);
      loadGrid();
    } catch (err) { toast.error(err.message); }
    finally { setAutoFilling(false); }
  };

  const days = (grid?.days || []).filter(d => {
    if (dayFrom && d.day < parseInt(dayFrom, 10)) return false;
    if (dayTo && d.day > parseInt(dayTo, 10)) return false;
    return true;
  });

  const handleImport = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    if (!window.confirm(`Import "${file.name}" into ${MONTHS[month]} ${year}? This replaces existing data for this month.`)) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', file); fd.append('month', month); fd.append('year', year); fd.append('sheet', sheetName);
    try {
      const r = await fetch(`${API}/attendance/import`, { method: 'POST', headers: authH(), body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Import failed');
      toast.success(`Imported ${j.employees} employees, ${j.records} records`);
      loadGrid();
    } catch (err) { toast.error(err.message); }
    finally { setImporting(false); }
  };

  const doExport = async (kind) => {
    try {
      const r = await fetch(`${API}/attendance/export/${kind}?month=${month}&year=${year}`, { headers: authH() });
      if (!r.ok) throw new Error('Export failed');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `attendance_${year}-${String(month).padStart(2, '0')}.${kind === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Exported');
    } catch (err) { toast.error(err.message); }
  };

  const openEdit = (emp, day) => {
    if (!canEdit) return;
    const rec = emp.records[String(day)] || {};
    setEditing({ empId: emp.id, empName: emp.name, day, status: rec.status || '', inTime: rec.inTime || '', outTime: rec.outTime || '' });
  };

  const saveEdit = async () => {
    try {
      const r = await fetch(`${API}/attendance/cell`, {
        method: 'PATCH', headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId: editing.empId, day: editing.day, status: editing.status, inTime: editing.inTime, outTime: editing.outTime }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Save failed');
      toast.success('Saved'); setEditing(null); loadGrid();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="space-y-5" data-testid="attendance-module">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-5 text-white shadow-lg">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Clock className="w-6 h-6" /> Employee Attendance</h1>
        <p className="text-slate-300 text-sm">Monthly attendance grid • color-coded • auto working-hour calculation</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[10px] uppercase text-slate-400 font-semibold block mb-1">Month</label>
          <select data-testid="filter-month" value={month} onChange={e => setMonth(+e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm">
            {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-400 font-semibold block mb-1">Year</label>
          <select data-testid="filter-year" value={year} onChange={e => setYear(+e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm">
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-[10px] uppercase text-slate-400 font-semibold block mb-1">Search Name / ID</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <Input data-testid="filter-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Employee name or ID" className="pl-8 h-9" />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-400 font-semibold block mb-1">Department</label>
          <Input data-testid="filter-dept" value={dept} onChange={e => setDept(e.target.value)} placeholder="Dept" className="h-9 w-28" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-400 font-semibold block mb-1">Status</label>
          <select data-testid="filter-status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm">
            <option value="">All</option>
            {STATUS_KEYS.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-400 font-semibold block mb-1">Days</label>
          <div className="flex items-center gap-1">
            <Input data-testid="filter-day-from" value={dayFrom} onChange={e => setDayFrom(e.target.value)} placeholder="1" className="h-9 w-14" />
            <span className="text-slate-400">–</span>
            <Input data-testid="filter-day-to" value={dayTo} onChange={e => setDayTo(e.target.value)} placeholder="31" className="h-9 w-14" />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 items-center">
        {canEdit && <>
          <Input value={sheetName} onChange={e => setSheetName(e.target.value)} placeholder="Sheet name (optional)" className="h-9 w-44" data-testid="import-sheet-name" />
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} className="hidden" />
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="import-btn">
            <Upload className="w-4 h-4 mr-1" />{importing ? 'Importing…' : 'Import Excel'}
          </Button>
        </>}
        <Button size="sm" variant="outline" onClick={() => doExport('excel')} data-testid="export-excel-btn"><Download className="w-4 h-4 mr-1" />Export Excel</Button>
        <Button size="sm" variant="outline" onClick={() => doExport('pdf')} data-testid="export-pdf-btn"><FileText className="w-4 h-4 mr-1" />Export PDF</Button>
        {/* Auto-Fill from Login Report */}
        {canEdit && (
          <div className="flex items-center gap-1.5 pl-2 border-l border-slate-200" data-testid="auto-fill-section">
            <input
              type="date"
              value={autoFillDate}
              onChange={e => setAutoFillDate(e.target.value)}
              data-testid="auto-fill-date"
              className="h-9 px-2 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
            <Button size="sm" onClick={handleAutoFill} disabled={autoFilling}
              data-testid="auto-fill-btn"
              className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white text-xs">
              <Clock className="w-3.5 h-3.5 mr-1" />{autoFilling ? 'Filling…' : 'Auto-Fill from Login'}
            </Button>
            {autoFillResult && (
              <span className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-1 rounded-full font-semibold" data-testid="auto-fill-result">
                Filled: {autoFillResult.filled} · Late: {autoFillResult.late} · Skipped: {autoFillResult.skipped}
              </span>
            )}
          </div>
        )}
        <div className="flex-1" />
        <div className="flex gap-1 text-[11px] items-center flex-wrap">
          {STATUS_KEYS.map(s => <span key={s} className={`px-2 py-0.5 rounded ${STATUS_META[s].cls}`}>{s} = {STATUS_META[s].label}</span>)}
          <span className="px-2 py-0.5 rounded bg-orange-400 text-white">LC = Late Coming</span>
        </div>
      </div>

      {/* Dashboard KPIs */}
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard icon={Users}    label="Total Employees" value={dash.totalEmployees} color="bg-slate-600" />
          <KpiCard icon={UserCheck} label="Present Today"   value={dash.presentToday}  color="bg-green-500" />
          <KpiCard icon={UserX}    label="Absent Today"    value={dash.absentToday}   color="bg-red-500" />
          <KpiCard icon={Plane}    label="On Leave"        value={dash.onLeave}       color="bg-yellow-400" />
          <KpiCard icon={Clock}    label="Avg Working Hrs" value={dash.avgWorkingHours} color="bg-blue-500" />
          <KpiCard icon={AlertTriangle} label="Late Coming" value={dash.lateCount}    color="bg-orange-500" />
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="py-16 text-center text-slate-400">Loading…</div>
      ) : !grid || grid.total === 0 ? (
        <div className="py-16 text-center text-slate-400 text-sm border rounded-xl bg-white">
          No attendance for {MONTHS[month]} {year}. {canEdit ? 'Use Import Excel to upload.' : ''}
        </div>
      ) : (
        <div className="border rounded-xl bg-white overflow-auto" style={{ maxHeight: '68vh' }} data-testid="attendance-grid">
          <table className="border-collapse text-[11px] whitespace-nowrap">
            <thead className="sticky top-0 z-20">
              <tr className="bg-slate-200">
                <th className="border px-2 py-1 sticky left-0 bg-slate-200 z-30" rowSpan={2}>SR</th>
                <th className="border px-2 py-1 sticky left-9 bg-slate-200 z-30 text-left" rowSpan={2} style={{ minWidth: 150 }}>EMPLOYEE</th>
                {days.map(d => <th key={d.day} className="border px-1 py-0.5 text-[9px] text-slate-600">{d.weekday.slice(0, 3).toUpperCase()}</th>)}
                <th className="border px-2 bg-slate-300" rowSpan={2}>P</th>
                <th className="border px-2 bg-slate-300" rowSpan={2}>A</th>
                <th className="border px-2 bg-slate-300" rowSpan={2}>L</th>
                <th className="border px-2 bg-slate-300" rowSpan={2}>H</th>
                <th className="border px-2 bg-slate-300" rowSpan={2}>WO</th>
                <th className="border px-2 bg-slate-300" rowSpan={2} style={{ minWidth: 64 }}>TOTAL Hrs</th>
                <th className="border px-2 bg-slate-300" rowSpan={2} style={{ minWidth: 56 }}>OT</th>
              </tr>
              <tr className="bg-slate-100">
                {days.map(d => <th key={d.day} className="border px-1 py-0.5 font-bold text-slate-700" style={{ minWidth: 46 }}>{d.day}</th>)}
              </tr>
            </thead>
            <tbody>
              {grid.employees.map((e, ei) => {
                const rec = (d) => e.records[String(d)] || {};
                const s = e.summary;
                return (
                  <React.Fragment key={e.id}>
                    <tr className="bg-orange-50" data-testid={`att-emp-row-${ei}`}>
                      <td className="border px-2 py-1 text-center font-bold sticky left-0 bg-orange-100 z-10" rowSpan={4}>{e.srNo}</td>
                      <td className="border px-2 py-1 sticky left-9 bg-orange-100 z-10 font-bold text-left">{e.name}</td>
                      {days.map(d => {
                        const st = rec(d.day).status || '';
                        return (
                          <td key={d.day}
                              onClick={() => openEdit(e, d.day)}
                              className={`border px-1 py-0.5 text-center font-bold ${STATUS_META[st]?.cls || ''} ${canEdit ? 'cursor-pointer hover:ring-2 hover:ring-slate-500' : ''}`}
                              data-testid={`cell-${ei}-${d.day}`}>{st}</td>
                        );
                      })}
                      <td className="border px-2 text-center font-semibold bg-green-50" rowSpan={4}>{s.P}</td>
                      <td className="border px-2 text-center font-semibold bg-red-50" rowSpan={4}>{s.A}</td>
                      <td className="border px-2 text-center font-semibold bg-yellow-50" rowSpan={4}>{s.L}</td>
                      <td className="border px-2 text-center font-semibold bg-blue-50" rowSpan={4}>{s.H}</td>
                      <td className="border px-2 text-center font-semibold bg-gray-100" rowSpan={4}>{s.WO}</td>
                      <td className="border px-2 text-center font-bold" rowSpan={4}>{s.totalFormatted}</td>
                      <td className="border px-2 text-center text-orange-600 font-semibold" rowSpan={4}>{s.overtimeFormatted}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-2 py-0.5 sticky left-9 bg-stone-50 text-left text-[10px] text-slate-500">IN-TIME</td>
                      {days.map(d => <td key={d.day} className="border px-1 py-0.5 text-center text-slate-600">{rec(d.day).inTime || ''}</td>)}
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-2 py-0.5 sticky left-9 bg-stone-50 text-left text-[10px] text-slate-500">OUT-TIME</td>
                      {days.map(d => <td key={d.day} className="border px-1 py-0.5 text-center text-slate-600">{rec(d.day).outTime || ''}</td>)}
                    </tr>
                    <tr className="bg-green-50">
                      <td className="border px-2 py-0.5 sticky left-9 bg-green-100 text-left text-[10px] font-semibold text-slate-600">TOTAL WORKING HOURS</td>
                      {days.map(d => <td key={d.day} className="border px-1 py-0.5 text-center text-slate-700">{rec(d.day).totalHours || '00:00'}</td>)}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {grid && grid.pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm text-slate-500">Page {grid.page} / {grid.pages} · {grid.total} employees</span>
          <Button size="sm" variant="outline" disabled={page >= grid.pages} onClick={() => setPage(p => p + 1)} data-testid="next-page"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}

      {/* Edit popover */}
      {editing && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl p-5 w-80 shadow-xl" onClick={e => e.stopPropagation()} data-testid="edit-cell-modal">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-sm">{editing.empName} · Day {editing.day}</h3>
              <button onClick={() => setEditing(null)}><X className="w-4 h-4" /></button>
            </div>
            <label className="text-[10px] uppercase text-slate-400 font-semibold">Status</label>
            <div className="flex gap-1 flex-wrap my-1">
              {['', ...STATUS_KEYS].map(s => (
                <button key={s || 'none'} onClick={() => setEditing(ed => ({ ...ed, status: s }))}
                        className={`px-2 py-1 rounded text-xs font-bold border ${editing.status === s ? 'ring-2 ring-slate-700' : ''} ${s ? STATUS_META[s].cls : 'bg-white text-slate-500'}`}
                        data-testid={`edit-status-${s || 'clear'}`}>{s || 'Clear'}</button>
              ))}
            </div>
            <label className="text-[10px] uppercase text-slate-400 font-semibold">In-Time</label>
            <Input value={editing.inTime} onChange={e => setEditing(ed => ({ ...ed, inTime: e.target.value }))} placeholder="e.g. 9:45 AM" className="h-9 mb-2" data-testid="edit-intime" />
            <label className="text-[10px] uppercase text-slate-400 font-semibold">Out-Time</label>
            <Input value={editing.outTime} onChange={e => setEditing(ed => ({ ...ed, outTime: e.target.value }))} placeholder="e.g. 8:10 PM" className="h-9 mb-3" data-testid="edit-outtime" />
            <Button size="sm" className="w-full" onClick={saveEdit} data-testid="edit-save-btn"><Save className="w-4 h-4 mr-1" />Save</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceModule;
